import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MicOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VideoTileProps {
  tileId: number;
  bindVideoElement: (tileId: number, element: HTMLVideoElement | null) => void;
  label?: string;
  isMuted?: boolean;
  isLocal?: boolean;
  className?: string;
}

export function VideoTile({
  tileId,
  bindVideoElement,
  label,
  isMuted,
  isLocal,
  className,
}: VideoTileProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      bindVideoElement(tileId, videoElement);
    }

    return () => {
      bindVideoElement(tileId, null);
    };
  }, [tileId, bindVideoElement]);

  const ariaLabel = isLocal
    ? t('videoSession.labels.yourVideoFeed', 'Your video feed')
    : label
      ? t('videoSession.labels.videoFeedFrom', 'Video feed from {{name}}', { name: label })
      : t('videoSession.labels.remoteParticipantVideoFeed', 'Remote participant video feed');

  const displayLabel = isMuted
    ? t('videoSession.labels.muted', '{{name}} (muted)', { name: label })
    : label;

  return (
    <div
      className={cn(
        'relative bg-gray-900 rounded-lg overflow-hidden',
        className
      )}
      role="region"
      aria-label={ariaLabel}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={cn(
          'w-full h-full object-cover',
          isLocal && 'scale-x-[-1]'
        )}
      />

      {label && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/60 text-white text-xs px-2 py-1 rounded">
          {isMuted && (
            <MicOff className="h-3 w-3 text-red-400" aria-hidden="true" />
          )}
          <span>{displayLabel}</span>
        </div>
      )}
    </div>
  );
}
